import type { UnitStep } from '../today/workflowBoard';

export type StageKey = 'booking' | 'dp' | 'agreement' | 'loan' | 'instalments' | 'deed' | 'possession';
export type StageState = 'done' | 'now' | 'soon';
export type LifecycleStage = { key: StageKey; title: string; state: StageState; steps: UnitStep[] };

export const STAGES: Array<{ key: StageKey; title: string }> = [
  { key: 'booking', title: 'Booking' },
  { key: 'dp', title: 'Down payment and allotment' },
  { key: 'agreement', title: 'Agreement' },
  { key: 'loan', title: 'Bank loan' },
  { key: 'instalments', title: 'Construction instalments' },
  { key: 'deed', title: 'Sale deed' },
  { key: 'possession', title: 'Possession and keys' },
];

// Order matters: the first rule that matches a step's key or title wins.
const RULES: Array<[StageKey, RegExp]> = [
  ['possession', /possession|handover|hand over|keys?\b|fit.?out/],
  ['deed', /deed/],
  // Allotment before agreement: "Signed Allotment Letter" would otherwise match "sign".
  ['dp', /allot|down.?payment|\bdp\b|\bgst\b/],
  ['agreement', /agreement|advocate|sign|regist/],
  ['instalments', /instal|demand|construction|full.?payment|slab|plinth/],
  ['booking', /kyc|customer|booking|form/],
];

/** Which stage a workflow step belongs to. Loan workflow steps always go to the loan stage. */
export function stageOfStep(step: Pick<UnitStep, 'workflowType' | 'key' | 'title'> & { stage?: string }, previous: StageKey | null): StageKey {
  if (step.workflowType === 'loan') return 'loan';
  // A stage picked in the workflow editor wins over guessing from the words.
  const picked = String(step.stage || '').trim();
  if (picked && STAGES.some((s) => s.key === picked)) return picked as StageKey;
  const text = `${step.key} ${step.title}`.toLowerCase().replace(/_/g, ' ');
  const hit = RULES.find(([, re]) => re.test(text));
  return hit ? hit[0] : previous || 'booking';
}

/**
 * Groups steps into the seven stages. A stage is done when all its steps are done, the first stage
 * that is not done is "now", and later ones are "soon". Stages with no steps take their state from
 * position (the loan stage is skipped when no loan is needed).
 */
export function buildLifecycle(steps: UnitStep[], opts: { loanNeeded: boolean; fullyPaid: boolean; deedDone: boolean }): LifecycleStage[] {
  const byStage = new Map<StageKey, UnitStep[]>(STAGES.map((s) => [s.key, []]));
  let previous: StageKey | null = null;
  steps
    .filter((s) => s.workflowType === 'main')
    .forEach((s) => {
      const k = stageOfStep(s, previous);
      byStage.get(k)!.push(s);
      previous = k;
    });
  steps.filter((s) => s.workflowType === 'loan').forEach((s) => byStage.get('loan')!.push(s));

  const stages = STAGES.filter((s) => s.key !== 'loan' || opts.loanNeeded);
  let nowFound = false;
  return stages.map((s) => {
    const own = byStage.get(s.key)!;
    let done = own.length ? own.every((x) => x.status === 'done') : false;
    if (!own.length) {
      if (s.key === 'instalments') done = opts.fullyPaid;
      if (s.key === 'deed') done = opts.deedDone;
    }
    // A later stage can finish before an earlier one (a loan sanctioned before the agreement), so done wins.
    // Stages also run side by side (the loan alongside the agreement), so any stage already under way is 'now' too.
    const underWay = own.some((x) => x.status === 'ready' || x.status === 'done');
    let state: StageState = 'soon';
    if (done) state = 'done';
    else if (!nowFound || underWay) {
      state = 'now';
      nowFound = true;
    }
    return { key: s.key, title: s.title, state, steps: own };
  });
}
